import { EnergyMediumSceneIds } from '@/api/energyMedium';
import {
  V2EnergyUnitElectricCostPostResponse,
  V2EnergyUnitTotalCostPostResponse,
  V2EnergyUnitMediumCostPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { isEmpty, isNil } from 'lodash-es';
import { DateType } from '../prop';
import { Themes } from '@maxtropy/components';
import { formatThousandUnit } from '@/utils/utils';

export const EnergyMediumSceneIdsColorDisplay = {
  [EnergyMediumSceneIds.WATER_SUPPLY_AND_DRAINAGE]: 'var(--mx-diagram1-color)',
  [EnergyMediumSceneIds.GAS_SUPPLY_AND_CONSUMPTION]: 'var(--mx-diagram3-color)',
  [EnergyMediumSceneIds.HOT_STEAM]: 'var(--mx-diagram9-color)',
  [EnergyMediumSceneIds.COMPRESSED_AIR]: 'var(--mx-diagram8-color)',
  [EnergyMediumSceneIds.INDUSTRIAL_GASES]: 'var(--mx-diagram4-color)',
  [EnergyMediumSceneIds.HEATING_VENTILATION_AND_AIR_CONDITIONING]: 'var(--mx-diagram13-color)',
  [EnergyMediumSceneIds.INDUSTRIAL_LIQUIDS]: 'var(--mx-diagram2-color)',
  [EnergyMediumSceneIds.ELEC]: 'var(--mx-diagram6-color)',
};

export const EnergyMediumSceneIdsPeriodColorDarkDisplay = {
  [EnergyMediumSceneIds.WATER_SUPPLY_AND_DRAINAGE]: 'rgba(82,231,255, 0.5)',
  [EnergyMediumSceneIds.GAS_SUPPLY_AND_CONSUMPTION]: 'rgba(255,203,71, 0.5)',
  [EnergyMediumSceneIds.HOT_STEAM]: 'rgba(255,146,71, 0.5)',
  [EnergyMediumSceneIds.COMPRESSED_AIR]: 'rgba(35,78,217, 0.5)',
  [EnergyMediumSceneIds.INDUSTRIAL_GASES]: 'rgba(111,71,255, 0.5)',
  [EnergyMediumSceneIds.HEATING_VENTILATION_AND_AIR_CONDITIONING]: 'rgba(89,215,68, 0.5)',
  [EnergyMediumSceneIds.INDUSTRIAL_LIQUIDS]: 'rgba(255,71,123, 0.5)',
  [EnergyMediumSceneIds.ELEC]: 'rgba(45,141,255, 0.5)',
};
export const EnergyMediumSceneIdsPeriodColorLightDisplay = {
  [EnergyMediumSceneIds.WATER_SUPPLY_AND_DRAINAGE]: 'rgba(125,216,230, 0.5)',
  [EnergyMediumSceneIds.GAS_SUPPLY_AND_CONSUMPTION]: 'rgba(250,204,88, 0.5)',
  [EnergyMediumSceneIds.HOT_STEAM]: 'rgba(245,143, 101, 0.5)',
  [EnergyMediumSceneIds.COMPRESSED_AIR]: 'rgba(88,114,197, 0.5)',
  [EnergyMediumSceneIds.INDUSTRIAL_GASES]: 'rgba(129,107,205, 0.5)',
  [EnergyMediumSceneIds.HEATING_VENTILATION_AND_AIR_CONDITIONING]: 'rgba(97,195,80, 0.5)',
  [EnergyMediumSceneIds.INDUSTRIAL_LIQUIDS]: 'rgba(248,112,150, 0.5)',
  [EnergyMediumSceneIds.ELEC]: 'rgba(105,177,255, 0.5)',
};

const seriesOption = ({
  axisInfo,
  data,
  avgCost,
  stack,
  theme,
}: {
  axisInfo: { name: string; id: number };
  data: any;
  avgCost?: string;
  stack?: string;
  theme: Themes;
}) => {
  return {
    name: `${axisInfo.name}`,
    type: 'bar',
    stack: stack,
    barMaxWidth: 80,
    emphasis: {
      focus: 'series',
    },
    data: data,
    markLine: avgCost
      ? {
          silent: true,
          symbol: ['none', 'none'],
          data: [
            {
              label: {
                formatter: (params: any) => {
                  return `均值: ${formatThousandUnit(params.value)}元`;
                },
                position: 'insideEndTop',
                color: theme === Themes.DARK ? '#59D744' : '#61C350',
                fontSize: 14,
                fontWeight: 600,
              },
              yAxis: avgCost,
              lineStyle: {
                type: 'dashed',
                color: theme === Themes.DARK ? '#59D744' : '#61C350',
              },
            },
          ],
        }
      : null,
  };
};

const momTitleFunc = (timeResolution: DateType) => {
  switch (timeResolution) {
    case DateType.DAY:
      return '环比昨日';
    case DateType.WEEK:
      return '环比上周';
    case DateType.MONTH:
      return '环比上月';
    case DateType.YEAR:
      return '环比上年';
    default:
      return '环比';
  }
};

export const getTotalOption = (data: V2EnergyUnitTotalCostPostResponse, timeResolution: DateType, theme: Themes) => {
  const chartData = data.chartList ?? [];

  if (isEmpty(chartData)) return;

  const colors: string[] = [];

  // 所有能源类型
  const energyTypes = (chartData[0]?.detailList ?? []).map(item => {
    colors.push(
      EnergyMediumSceneIdsColorDisplay[item.sceneId as EnergyMediumSceneIds] ??
        EnergyMediumSceneIdsColorDisplay['elec' as EnergyMediumSceneIds]
    );
    return {
      id: item.energyMediumId!,
      name: `${item.energyMediumName}成本`,
    };
  });

  // 时段所有能源类型
  const periodEnergyTypes = (chartData[0]?.periodCost?.detailList ?? []).map(item => {
    colors.push(
      (theme === Themes.DARK
        ? EnergyMediumSceneIdsPeriodColorDarkDisplay
        : EnergyMediumSceneIdsPeriodColorLightDisplay)[item.sceneId as EnergyMediumSceneIds] ??
        (theme === Themes.DARK
          ? EnergyMediumSceneIdsPeriodColorDarkDisplay
          : EnergyMediumSceneIdsPeriodColorLightDisplay)['elec' as EnergyMediumSceneIds]
    );
    return {
      id: item.energyMediumId!,
      name: `${chartData[0]?.periodCost?.periodName}-${item.energyMediumName}成本`,
    };
  });

  const isShowYoy = timeResolution === DateType.WEEK || timeResolution === DateType.MONTH;

  return {
    legend: {
      right: 80,
      top: 4,
      itemWidth: 12,
      itemHeight: 12,
      type: 'scroll',
    },
    grid: {
      top: 80,
      bottom: 20,
      containLabel: true,
    },
    color: colors,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const dataIndex = params[0].dataIndex;
        const item = chartData[dataIndex];

        return `
          <div style="font-size:12px;">
            <div>${item.time}</div>
            <table>
              <tr style="text-align: right;">
                <td style="padding: 0 8px"></td>
                <td style="padding: 0 8px">成本</td>
                <td style="padding: 0 8px">${momTitleFunc(timeResolution)}</td>
                ${isShowYoy ? `<td style="padding: 0 8px">同比上年</td>` : ''}
              </tr>
              <tr style="text-align: right;">
                <td style="padding: 0 8px 0 24px;text-align: left">总成本</td>
                <td style="padding: 0 8px;text-align: left;">
                  ${formatThousandUnit(item.totalCost)}元
                </td>
                <td style="padding: 0 8px;color:${Number(item.momValue ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                  ${item.momValue ?? '--'}%
                </td>
                ${
                  isShowYoy
                    ? `  <td style="padding: 0 8px;color:${Number(item.yoyValue ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                    ${item.yoyValue ?? '--'}%</td>`
                    : ''
                }
              </tr>
               ${params
                 .filter((param: any) => !param.data?.isPeriod)
                 .map((param: any, index: number) => {
                   return `
                  <tr style="text-align: right;">
                <td style="padding: 0 8px;text-align: left;">${param.marker}${param.seriesName}</td>
                <td style="padding: 0 8px;color: ${
                  theme === Themes.DARK ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'
                }">
                  ${formatThousandUnit(param.value)}元
                </td>
                <td style="padding: 0 8px;color:${Number(param.data.momValue ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                  ${param.data.momValue ?? '--'}%
                </td>
                ${
                  isShowYoy
                    ? `<td style="padding: 0 8px;color:${Number(param.data.yoyValue ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                    ${param.data.yoyValue ?? '--'}%</td>`
                    : ''
                }
              </tr>
                  `;
                 })
                 .join('')}
              
              ${
                params.find((param: any) => param.data?.isPeriod)
                  ? `<tr style="height:1px; background: rgba(255,255,255,0.2);margin: 6px 0">
                    <td style="padding: 0 8px 0 24px;text-align: left"></td>
                    <td style="padding: 0 8px;color: #FFCB47;"></td>
                    <td style="padding: 0 8px;"></td>
                    ${isShowYoy ? `<td style="padding: 0 8px;"></td>` : ''}
                  </tr>
                  <tr style="text-align: right;">
                  <td style="padding: 0 8px 0 24px;text-align: left">${item.periodCost!.periodName}-总成本</td>
                  <td style="padding: 0 8px;color: ${
                    theme === Themes.DARK ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'
                  }">
                    ${formatThousandUnit(item.periodCost!.totalCost)}元
                  </td>
                  <td style="padding: 0 8px;color:${
                    Number(item.periodCost!.momValue ?? 0) > 0 ? '#E64242' : '#52C41A'
                  }">
                    ${item.periodCost!.momValue ?? '--'}%
                  </td>
                ${
                  isShowYoy
                    ? `<td style="padding: 0 8px;color:${
                        Number(item.periodCost!.yoyValue ?? 0) > 0 ? '#E64242' : '#52C41A'
                      }">
                    ${item.periodCost!.yoyValue ?? '--'}%</td>`
                    : ''
                }
                </tr>
                  `
                  : ''
              }
              ${params
                .filter((param: any) => param.data?.isPeriod)
                .map((param: any, index: number) => {
                  return `
                  <tr style="text-align: right;">
                <td style="padding: 0 8px;text-align: left;">${param.marker}${param.seriesName}</td>
                <td style="padding: 0 8px;color: ${
                  theme === Themes.DARK ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'
                }">
                  ${formatThousandUnit(param.value)}元
                </td>
                <td style="padding: 0 8px;color:${Number(param.data.momValue ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                  ${param.data.momValue ?? '--'}%
                </td>
                ${
                  isShowYoy
                    ? `<td style="padding: 0 8px;color:${Number(param.data.yoyValue ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                    ${param.data.yoyValue ?? '--'}%</td>`
                    : ''
                }
              </tr>
                  `;
                })
                .join('')}
            </table>
          </div>
        `;
      },
    },
    toolbox: {
      width: 86,
      height: 100,
      right: 10,
      itemSize: 20, // 设置图标大小
      feature: {
        brush: {
          type: ['lineX', 'clear'], // 选择工具类型
          title: {
            lineX: '横向选择',
            clear: '清除',
          },
        },
      },
    },
    brush: {
      toolbox: ['lineX', 'clear'],
      xAxisIndex: 0,
    },
    xAxis: {
      type: 'category',
      data: chartData?.map(i => i.time),
      textStyle: {
        color: 'var(--mx-text-secondary-color)',
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        textStyle: {
          color: 'var(--mx-text-secondary-color)',
        },
        formatter: (name: string) => name.split('（')[0],
      },
    },
    yAxis: {
      type: 'value',
      name: '元',
      nameGap: 30,
    },
    series: [
      ...energyTypes.map((info, index) =>
        seriesOption({
          axisInfo: info,
          data: chartData.map(item => {
            const found = (item.detailList ?? []).find(d => d.energyMediumId === info.id);
            return found
              ? {
                  energyMediumId: found.energyMediumId!,
                  value: found.energyUseCost,
                  momValue: found.momValue,
                  yoyValue: found.yoyValue,
                  name: `${found.energyMediumName}成本`,
                }
              : {};
          }),
          avgCost: data.avgCost ?? '0',
          stack: '成本',
          theme,
        })
      ),
      ...periodEnergyTypes.map((info, index) =>
        seriesOption({
          axisInfo: info,
          data: chartData.map(item => {
            const found = (item.periodCost?.detailList ?? []).find(d => d.energyMediumId === info.id);
            return found
              ? {
                  energyMediumId: found.energyMediumId!,
                  value: found.energyUseCost,
                  momValue: found.momValue,
                  yoyValue: found.yoyValue,
                  name: `${found.energyMediumName}成本`,
                  isPeriod: true,
                }
              : {};
          }),
          stack: '时段',
          theme,
        })
      ),
    ],
  };
};

export const getElectricOption = (
  data: V2EnergyUnitElectricCostPostResponse,
  timeResolution: DateType,
  theme: Themes
) => {
  const chartData = data.chartList ?? [];

  if (isEmpty(chartData)) return;

  // 提取堆叠数据
  const summitData = chartData.map(item => {
    return {
      momValue: item.detail?.summitMomValue,
      yoyValue: item.detail?.summitYoyValue,
      value: item.detail?.summitCost,
    };
  });
  const peakData = chartData.map(item => {
    return {
      momValue: item.detail?.peakMomValue,
      yoyValue: item.detail?.peakYoyValue,
      value: item.detail?.peakCost,
    };
  });
  const plainData = chartData.map(item => {
    return {
      momValue: item.detail?.plainMomValue,
      yoyValue: item.detail?.plainYoyValue,
      value: item.detail?.plainCost,
    };
  });
  const valleyData = chartData.map(item => {
    return {
      momValue: item.detail?.valleyMomValue,
      yoyValue: item.detail?.valleyYoyValue,
      value: item.detail?.valleyCost,
    };
  });

  // 时段堆叠数据
  const periodSummitData = chartData.map(item => {
    return {
      momValue: item.periodCost?.detail?.summitMomValue,
      yoyValue: item.periodCost?.detail?.summitYoyValue,
      value: item.periodCost?.detail?.summitCost,
      isPeriod: true,
    };
  });
  const periodPeakData = chartData.map(item => {
    return {
      momValue: item.periodCost?.detail?.peakMomValue,
      yoyValue: item.periodCost?.detail?.peakYoyValue,
      value: item.periodCost?.detail?.peakCost,
      isPeriod: true,
    };
  });
  const periodPlainData = chartData.map(item => {
    return {
      momValue: item.periodCost?.detail?.plainMomValue,
      yoyValue: item.periodCost?.detail?.plainYoyValue,
      value: item.periodCost?.detail?.plainCost,
      isPeriod: true,
    };
  });
  const periodValleyData = chartData.map(item => {
    return {
      momValue: item.periodCost?.detail?.valleyMomValue,
      yoyValue: item.periodCost?.detail?.valleyYoyValue,
      value: item.periodCost?.detail?.valleyCost,
      isPeriod: true,
    };
  });

  const isShowYoy = timeResolution === DateType.WEEK || timeResolution === DateType.MONTH;

  return {
    legend: {
      right: 80,
      top: 4,
      itemWidth: 12,
      itemHeight: 12,
      type: 'scroll',
    },
    grid: {
      top: 80,
      bottom: 20,
      containLabel: true,
    },
    color: [
      'var(--mx-diagram17-color)',
      'var(--mx-diagram18-color)',
      'var(--mx-diagram19-color)',
      'var(--mx-diagram20-color)',
      'rgba(255,109,44, 0.5)',
      'rgba(206,144,209, 0.5)',
      'rgba(13,182,217,0.5)',
      'rgba(99,188,127, 0.5)',
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const dataIndex = params[0].dataIndex;
        const item = chartData[dataIndex];

        return `
          <div style="font-size:12px;">
            <div>${item.time}</div>
            <table>
              <tr style="text-align: right;">
                <td style="padding: 0 8px"></td>
                <td style="padding: 0 8px">成本</td>
                <td style="padding: 0 8px">${momTitleFunc(timeResolution)}</td>
                ${isShowYoy ? `<td style="padding: 0 8px">同比上年</td>` : ''}
              </tr>
              <tr style="text-align: right;">
                <td style="padding: 0 8px 0 24px;text-align: left">总成本</td>
                <td style="padding: 0 8px;text-align: left;">
                  ${formatThousandUnit(item.totalCost)}元
                </td>
                <td style="padding: 0 8px;color:${Number(item.momValue ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                  ${item.momValue ?? '--'}%
                </td>
                ${
                  isShowYoy
                    ? `  <td style="padding: 0 8px;color:${Number(item.yoyValue ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                    ${item.yoyValue ?? '--'}%</td>`
                    : ''
                }
              </tr>
               ${params
                 .filter((param: any) => !param.data?.isPeriod)
                 .map((param: any, index: number) => {
                   return `
                  <tr style="text-align: right;">
                <td style="padding: 0 8px;text-align: left;">${param.marker}${param.seriesName}</td>
                <td style="padding: 0 8px;color: ${
                  theme === Themes.DARK ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'
                }">
                  ${formatThousandUnit(param.value)}元
                </td>
                <td style="padding: 0 8px;color:${Number(param.data.momValue ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                  ${param.data.momValue ?? '--'}%
                </td>
                ${
                  isShowYoy
                    ? `<td style="padding: 0 8px;color:${Number(param.data.yoyValue ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                    ${param.data.yoyValue ?? '--'}%</td>`
                    : ''
                }
              </tr>
                  `;
                 })
                 .join('')}
                ${
                  params.find((param: any) => param.data?.isPeriod)
                    ? `<tr style="height:1px; background: rgba(255,255,255,0.2);margin: 6px 0">
                    <td style="padding: 0 8px 0 24px;text-align: left"></td>
                    <td style="padding: 0 8px;color: #FFCB47;"></td>
                    <td style="padding: 0 8px;"></td>
                    ${isShowYoy ? `<td style="padding: 0 8px;"></td>` : ''}
                  </tr>
                  <tr style="text-align: right;">
                  <td style="padding: 0 8px 0 24px;text-align: left">${item.periodCost!.periodName}-总成本</td>
                  <td style="padding: 0 8px;color: ${
                    theme === Themes.DARK ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'
                  }">
                    ${formatThousandUnit(item.periodCost!.totalCost)}元
                  </td>
                  <td style="padding: 0 8px;color:${
                    Number(item.periodCost!.momValue ?? 0) > 0 ? '#E64242' : '#52C41A'
                  }">
                    ${item.periodCost!.momValue ?? '--'}%
                  </td>
                ${
                  isShowYoy
                    ? `<td style="padding: 0 8px;color:${
                        Number(item.periodCost!.yoyValue ?? 0) > 0 ? '#E64242' : '#52C41A'
                      }">
                    ${item.periodCost!.yoyValue ?? '--'}%</td>`
                    : ''
                }
                </tr>
                  `
                    : ''
                }
              ${params
                .filter((param: any) => param.data?.isPeriod)
                .map((param: any, index: number) => {
                  return `
                  <tr style="text-align: right;">
                <td style="padding: 0 8px;text-align: left;">${param.marker}${param.seriesName}</td>
                <td style="padding: 0 8px;color: ${
                  theme === Themes.DARK ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'
                }">
                  ${formatThousandUnit(param.value)}元
                </td>
                <td style="padding: 0 8px;color:${Number(param.data.momValue ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                  ${param.data.momValue ?? '--'}%
                </td>
                ${
                  isShowYoy
                    ? `<td style="padding: 0 8px;color:${Number(param.data.yoyValue ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                    ${param.data.yoyValue ?? '--'}%</td>`
                    : ''
                }
              </tr>
                  `;
                })
                .join('')}
            </table>
          </div>
        `;
      },
    },
    toolbox: {
      width: 86,
      height: 100,
      right: 10,
      itemSize: 20, // 设置图标大小
      feature: {
        brush: {
          type: ['lineX', 'clear'], // 选择工具类型
          title: {
            lineX: '横向选择',
            clear: '清除',
          },
        },
      },
    },
    brush: {
      toolbox: ['lineX', 'clear'],
      xAxisIndex: 0,
    },
    xAxis: {
      type: 'category',
      data: chartData?.map(i => i.time),
      textStyle: {
        color: 'var(--mx-text-secondary-color)',
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        textStyle: {
          color: 'var(--mx-text-secondary-color)',
        },
        formatter: (name: string) => name.split('（')[0],
      },
    },
    yAxis: {
      type: 'value',
      name: '元',
      nameGap: 30,
    },
    series: [
      {
        name: '尖时段成本',
        type: 'bar',
        stack: '总成本',
        barMaxWidth: 80,
        data: summitData,
      },
      {
        name: '峰时段成本',
        type: 'bar',
        stack: '总成本',
        data: peakData,
      },
      {
        name: '平时段成本',
        type: 'bar',
        stack: '总成本',
        data: plainData,
      },
      {
        name: '谷时段成本',
        type: 'bar',
        stack: '总成本',
        data: valleyData,
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          data: [
            {
              label: {
                formatter: (params: any) => {
                  return `均值: ${formatThousandUnit(params.value)}元`;
                },
                position: 'insideEndTop',
                fontSize: 14,
                fontWeight: 600,
                color: theme === Themes.DARK ? '#59D744' : '#61C350',
              },
              yAxis: data?.avgCost ?? 0,
              lineStyle: {
                type: 'dashed',
                color: theme === Themes.DARK ? '#59D744' : '#61C350',
              },
            },
          ],
        },
      },
      ...(chartData[0]?.periodCost?.detail
        ? [
            {
              name: `${chartData[0]?.periodCost?.periodName} - 尖时段成本`,
              type: 'bar',
              barMaxWidth: 80,
              stack: '时段',
              data: periodSummitData,
              encode: { y: 'value' },
            },
            {
              name: `${chartData[0]?.periodCost?.periodName} - 峰时段成本`,
              type: 'bar',
              stack: '时段',
              data: periodPeakData,
              encode: { y: 'value' },
            },
            {
              name: `${chartData[0]?.periodCost?.periodName} - 平时段成本`,
              type: 'bar',
              stack: '时段',
              data: periodPlainData,
              encode: { y: 'value' },
            },
            {
              name: `${chartData[0]?.periodCost?.periodName} - 谷时段成本`,
              type: 'bar',
              stack: '时段',
              data: periodValleyData,
              encode: { y: 'value' },
            },
          ]
        : []),
    ],
  };
};

export const getBaseOption = (data: V2EnergyUnitMediumCostPostResponse, timeResolution: DateType, theme: Themes) => {
  const chartData = data?.chartList ?? [];

  if (isEmpty(chartData)) return;
  const colors = [
    EnergyMediumSceneIdsColorDisplay[data.sceneId as EnergyMediumSceneIds],
    theme === Themes.DARK
      ? EnergyMediumSceneIdsPeriodColorDarkDisplay[data.sceneId as EnergyMediumSceneIds]
      : EnergyMediumSceneIdsPeriodColorLightDisplay[data.sceneId as EnergyMediumSceneIds],
  ];

  // 介质数据
  const mediumData = chartData.map(item => ({
    value: item.energyUseCost,
    momValue: item.momValue,
    yoyValue: item.yoyValue,
    name: `${data.energyMediumName}成本`,
  }));

  // 时段介质数据
  const periodData = chartData.map(item => ({
    value: item.periodCost?.energyUseCost,
    momValue: item.periodCost?.momValue,
    yoyValue: item.periodCost?.yoyValue,
    name: `${item.periodCost?.periodName}-${data.energyMediumName}成本`,
  }));

  const isShowYoy = timeResolution === DateType.WEEK || timeResolution === DateType.MONTH;

  return {
    grid: {
      containLabel: true,
      bottom: 20,
    },
    color: colors,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        return `
          <div style="font-size:12px;">
            <div>${params[0]?.axisValue}</div>
            <table>
              <tr style="text-align: right;">
                <td style="padding: 0 8px"></td>
                <td style="padding: 0 8px">成本</td>
                <td style="padding: 0 8px">${momTitleFunc(timeResolution)}</td>
                ${isShowYoy ? `<td style="padding: 0 8px">同比上年</td>` : ''}
              </tr>
              ${params
                .map((item: any) => {
                  const { marker, data } = item;
                  const { momValue, name, value, yoyValue } = data ?? {};
                  return `
                  <tr style="text-align: right;">
                    <td style="padding: 0 8px;text-align: left;">${marker}${name}</td>
                    <td style="padding: 0 8px;color: ${
                      theme === Themes.DARK ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'
                    }">
                      ${formatThousandUnit(value)}元
                    </td>
                    <td style="padding: 0 8px;color:${Number(momValue ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                      ${momValue ?? '--'}%
                    </td>
                    ${
                      isShowYoy
                        ? `<td style="padding: 0 8px;color:${Number(yoyValue ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                        ${yoyValue ?? '--'}%</td>`
                        : ''
                    }
                  </tr>
                `;
                })
                .join('')}
            </table>
          </div>
        `;
      },
    },
    toolbox: {
      width: 200,
      height: 100,
      right: 10,
      itemSize: 20, // 设置图标大小
      feature: {
        brush: {
          type: ['lineX', 'clear'], // 选择工具类型
          title: {
            lineX: '横向选择',
            clear: '清除',
          },
        },
      },
    },
    brush: {
      toolbox: ['lineX', 'clear'],
      xAxisIndex: 0,
    },
    xAxis: {
      type: 'category',
      data: chartData?.map(i => i.time),
      textStyle: {
        color: 'var(--mx-text-secondary-color)',
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        textStyle: {
          color: 'var(--mx-text-secondary-color)',
        },
        formatter: (name: string) => name.split('（')[0],
      },
    },
    yAxis: {
      type: 'value',
      name: '元',
      nameGap: 30,
    },
    series: [
      {
        name: `${mediumData[0]?.name}`,
        type: 'bar',
        barMaxWidth: 80,
        emphasis: {
          focus: 'series',
        },
        data: mediumData,
        encode: { y: 'value' },
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          data: [
            {
              label: {
                formatter: (params: any) => {
                  return `均值: ${formatThousandUnit(params.value)}元`;
                },
                position: 'insideEndTop',
                color: theme === Themes.DARK ? '#59D744' : '#61C350',
                fontSize: 14,
                fontWeight: 600,
              },
              yAxis: data.avgCost ?? 0,
              lineStyle: {
                type: 'dashed',
                color: theme === Themes.DARK ? '#59D744' : '#61C350',
              },
            },
          ],
        },
      },
      isNil(data?.chartList?.[0]?.periodCost)
        ? {}
        : {
            name: `${periodData[0]?.name}`,
            type: 'bar',
            barMaxWidth: 80,
            emphasis: {
              focus: 'series',
            },
            data: periodData,
            encode: { y: 'value' },
          },
    ],
  };
};
